import React, { CSSProperties, MouseEvent } from "react";
import { ColumnDef, Row } from "@tanstack/react-table";
import {
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import type { UniqueIdentifier } from "@dnd-kit/core";
import TbodyCell from "../Tbody/TbodyCell";

export interface TrowProps {
  row: Row<unknown>;
  rowHeight?: number | string;
  onRowClick?: ((row: Row<unknown>) => void) | null;
  "data-testid"?: string;
  style?: CSSProperties;
  isSelected?: boolean;
  enableTableViewEdit?: boolean;
  columns?: ColumnDef<unknown, unknown>[];
  lastPinnedColumn?: number;
}

const Trow: React.FC<TrowProps> = ({
  row,
  rowHeight,
  onRowClick = null,
  "data-testid": dataTestId,
  style,
  isSelected,
  enableTableViewEdit,
  columns,
  lastPinnedColumn,
}) => {
  // Rows with a sub-component expand via their own toggle, not a row click.
  const togglesOnRowClick = row.getCanExpand() && row.subRows.length > 0;

  return (
    <tr
      key={row.id}
      className={`tr ${onRowClick ? "clickable" : ""} ${
        row.getIsSelected() ? "checked" : ""
      } ${isSelected ? "selected" : ""}`}
      data-testid={`${dataTestId}-row-${row?.index}`}
      onClick={
        onRowClick
          ? (e: MouseEvent<HTMLTableRowElement>) => {
              e.stopPropagation();
              onRowClick?.(row);
              if (togglesOnRowClick) {
                row?.getToggleExpandedHandler()?.();
              }
            }
          : () => {
              if (togglesOnRowClick) {
                row.getToggleExpandedHandler()?.();
              }
            }
      }
      style={{ ...style, cursor: togglesOnRowClick ? "pointer" : "normal" }}
    >
      {row.getVisibleCells().map((cell) => (
        <SortableContext
          key={cell.id}
          items={
            enableTableViewEdit
              ? // columns are ColumnDef objects with an `id`; dnd-kit accepts {id} items
                ((columns || []) as unknown as UniqueIdentifier[])
              : []
          }
          strategy={horizontalListSortingStrategy}
        >
          <TbodyCell
            cell={cell}
            rowHeight={rowHeight}
            dataTestId={dataTestId}
            enableTableViewEdit={enableTableViewEdit}
            lastPinnedColumn={lastPinnedColumn}
          />
        </SortableContext>
      ))}
    </tr>
  );
};

export default Trow;
